/**
 * useScreenNavigation — Owns multi-screen tab navigation (taskbar) for the app.
 *
 * Manages:
 *   - screens[] (open tabs)
 *   - activeScreenId
 *   - navigationHistory (back-stack)
 *   - hubNavigationState (last module / submodule / expanded for HUB)
 *
 * Exposes: navigateTo, closeScreen, goBack, resetToDashboard
 */
import { useCallback, useEffect, useState } from 'react';
import { ModuleId } from '@/types';
import { NAVIGATION_ITEMS } from '@/constants';
import SessionService from '@/services/SessionService';

// Hub item-id / sidebar screen-id → canonical ModuleId
const SCREEN_RESOLVE = {
  // Hub setup items → ModuleId
  'sm-uams-grp': 'USER_GROUPS', 'sm-uams-rol': 'USER_ROLES', 'sm-uams-lvl': 'USER_LEVELS',
  'sm-uams-usr': 'USER_CREATION', 'sm-uams-prv': 'USER_PRIVILEGES', 'sm-uams-ctrl': 'INDUSTRY_CONFIG',
  'sm-set-mod': 'MODULE_MASTER', 'sm-set-sub': 'SUBMODULE_MASTER', 'sm-set-scr': 'SCREEN_MASTER',
  'sm-prof': 'USER_PROFILES', 'sm-bday': 'DAY_OPEN', 'sm-day-open': 'DAY_OPEN', 'sm-day-close': 'DAY_CLOSE',
  // Sidebar screen IDs → ModuleId
  '21.1.1': 'USER_GROUPS', '21.1.2': 'USER_ROLES', '21.1.4': 'USER_LEVELS',
  '21.1.5': 'USER_CREATION', '21.1.7': 'USER_PRIVILEGES', '21.1.8': 'INDUSTRY_CONFIG',
  '21.2.1': 'MODULE_MASTER', '21.2.2': 'SUBMODULE_MASTER', '21.2.3': 'SCREEN_MASTER',
  '21.7.6': 'USER_PROFILES',
};

const DEFAULT_HUB_STATE = {
  lastModule: null,
  lastSubModule: null,
  expandedModules: {},
  activeTab: 'apps',
};

const DASHBOARD_SCREEN = { id: 'dash-main', moduleId: ModuleId.DASHBOARD, title: 'Dashboard' };

export default function useScreenNavigation() {
  const [hubNavigationState, setHubNavigationState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('hubNavigationState');
      return saved ? JSON.parse(saved) : { ...DEFAULT_HUB_STATE };
    } catch { return { ...DEFAULT_HUB_STATE }; }
  });
  const [screens, setScreens] = useState([DASHBOARD_SCREEN]);
  const [activeScreenId, setActiveScreenId] = useState('dash-main');
  const [navigationHistory, setNavigationHistory] = useState([]);

  // Persist hub state
  useEffect(() => {
    sessionStorage.setItem('hubNavigationState', JSON.stringify(hubNavigationState));
  }, [hubNavigationState]);

  const navigateTo = useCallback((moduleId, subItemId) => {
    const resolvedModuleId = (subItemId && SCREEN_RESOLVE[subItemId]) ? SCREEN_RESOLVE[subItemId] : moduleId;

    // If already open, just switch to it (and push history)
    const screenKey = subItemId || resolvedModuleId;
    let switched = false;
    setScreens(prev => {
      const existing = prev.find((s) => s.id === screenKey);
      if (existing) {
        switched = true;
        setNavigationHistory(h => [...h, activeScreenId]);
        setActiveScreenId(existing.id);
        return prev;
      }
      return prev;
    });
    if (switched) return;

    // Resolve title from NAVIGATION_ITEMS
    let title = moduleId;
    for (const navItem of NAVIGATION_ITEMS) {
      if (navItem.id === moduleId) { title = navItem.label; break; }
      for (const sec of navItem.sections) {
        const sub = sec.items.find((i) => i.id === subItemId);
        if (sub) { title = sub.label; break; }
      }
    }

    setNavigationHistory(prev => [...prev, activeScreenId]);
    const newScreen = { id: subItemId || resolvedModuleId, moduleId: resolvedModuleId, subItemId, title };
    setScreens(prev => [...prev, newScreen]);
    setActiveScreenId(newScreen.id);

    SessionService.saveRecentActivity({
      id: Date.now(),
      moduleId: resolvedModuleId,
      subItemId,
      title,
      module: moduleId,
      type: 'screen',
    });

    if (moduleId && moduleId !== ModuleId.HUB) {
      setHubNavigationState(prev => ({
        ...prev,
        lastModule: moduleId,
        lastSubModule: subItemId,
        expandedModules: { ...prev.expandedModules, [moduleId]: true },
      }));
      sessionStorage.setItem('currentModule', moduleId);
      if (subItemId) sessionStorage.setItem('currentSubModule', subItemId);
    }
  }, [activeScreenId]);

  const goBack = useCallback(() => {
    if (navigationHistory.length === 0) return false;
    const previousScreenId = navigationHistory[navigationHistory.length - 1];
    setNavigationHistory(prev => prev.slice(0, -1));
    setActiveScreenId(previousScreenId);
    return true;
  }, [navigationHistory]);

  const closeScreen = useCallback((screenId) => {
    let closingScreen;
    setScreens(prev => {
      closingScreen = prev.find(s => s.id === screenId);
      const newScreens = prev.filter(s => s.id !== screenId);

      if (closingScreen?.moduleId) {
        // Determine which hub tab to return to based on the module being closed
        const DYNAMIC_ATTRS_MODULES = ['DYNAMIC_ATTRIBUTES', 'ATTRIBUTE_STUDIO', 'HELP_ENGINE'];
        const returnTab = DYNAMIC_ATTRS_MODULES.includes(closingScreen.moduleId)
          ? 'dynamic_attributes'
          : 'setup';
        setHubNavigationState(state => {
          const next = {
            ...state,
            lastModule: closingScreen.moduleId,
            lastSubModule: closingScreen.subItemId,
            activeTab: returnTab,
            expandedModules: { ...state.expandedModules, [closingScreen.moduleId]: true },
          };
          sessionStorage.setItem('hubNavigationState', JSON.stringify(next));
          return next;
        });
      }

      if (activeScreenId === screenId && newScreens.length > 0) {
        // try history first
        if (navigationHistory.length > 0) {
          const previousScreenId = navigationHistory[navigationHistory.length - 1];
          const previousExists = newScreens.find(s => s.id === previousScreenId);
          if (previousExists) {
            setNavigationHistory(h => h.slice(0, -1));
            setActiveScreenId(previousScreenId);
            return newScreens;
          }
        }
        setActiveScreenId(newScreens[newScreens.length - 1].id);
      }
      return newScreens;
    });
  }, [activeScreenId, navigationHistory]);

  const resetToDashboard = useCallback(() => {
    setScreens([DASHBOARD_SCREEN]);
    setActiveScreenId('dash-main');
    setNavigationHistory([]);
  }, []);

  const activeScreen = screens.find(s => s.id === activeScreenId);

  return {
    screens,
    activeScreenId,
    setActiveScreenId,
    activeScreen,
    navigationHistory,
    hubNavigationState,
    setHubNavigationState,
    navigateTo,
    closeScreen,
    goBack,
    resetToDashboard,
  };
}
